// # Tooltip — 마우스를 올리거나 키보드로 닿으면 뜨는 한 줄(docs/design.md)
//
// `popover="manual"` 로 맨 위 층에 띄운다. `auto` 가 아닌 까닭은 둘이다 — 뜨면서 열려 있던 메뉴를
// 닫아 버리면 안 되고, 바깥 누르기로 닫히는 대상도 아니다. 자리는 `Popover` 와 같은 `put_beside`.
//
// - 한 줄 설명이지 내용이 아니다. 누를 것·고를 것을 넣지 않는다(마우스가 옮겨 가면 사라진다).
// - 마우스는 0.5초 기다렸다 뜬다(`TOOLTIP_DELAY_MS`). 하나가 떠 있다 막 닫혔으면 다음은 곧바로 —
//   도구 줄을 훑을 때 매번 기다리게 하지 않는다. 손가락(터치)은 지나가기가 없다 — 대신 **누르고 있으면**
//   같은 0.5초 뒤에 뜨고 떼면 사라진다(iOS 의 길게 누르기와 같다). 짧게 누르면(단추를 누른 것) 뜨지 않는다.
// - 키보드는 `:focus-visible` 일 때만 곧바로 뜬다. 마우스로 눌러 생긴 초점에 뜨면 누를 때마다 깜빡인다.
// - Esc 로 닫는다(WCAG 1.4.13). 스크린리더에는 `aria-describedby` 로 이어 준다.
// - 감싼 요소의 `title` 은 걷는다. 두 개가 겹쳐 뜨면 안 된다.
// - 감싸는 틀을 두지 않는다. 풍선은 감싼 요소 **바로 뒤의 형제**이고(닫혀 있으면 `display: none`),
//   사건 손잡이는 감싼 요소에 덧붙인다. 틀이 끼면 `.도구줄 > .Button` 같은 선택자와 부모의 `gap`·
//   `margin-inline-start: auto` 가 단추에 닿지 않는다. 자리는 풍선의 앞 형제로 잰다.

import {
  cloneElement,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react'
import { cx } from './cx.ts'
import { type PopAlign, type PopSide, put_beside } from './popover.tsx'

export const TOOLTIP_DELAY_MS = 500
/** 이만큼 안에 다른 풍선이 닫혔으면 기다리지 않는다. */
export const TOOLTIP_SKIP_MS = 300

let last_hidden = Number.NEGATIVE_INFINITY

/** 지금 띄우기까지 기다릴 시간. */
export function tooltip_wait(now: number, hidden_at: number): number {
  return now - hidden_at < TOOLTIP_SKIP_MS ? 0 : TOOLTIP_DELAY_MS
}

/** 감싼 요소에서 엿듣는 것들 — 원래 손잡이는 그대로 먼저 불린다. */
interface Anchor {
  'aria-describedby'?: string
  title?: string | undefined
  onPointerEnter?(e: PointerEvent<never>): void
  onPointerLeave?(e: PointerEvent<never>): void
  onPointerDown?(e: PointerEvent<never>): void
  onPointerUp?(e: PointerEvent<never>): void
  onPointerCancel?(e: PointerEvent<never>): void
  onFocus?(e: FocusEvent<never>): void
  onBlur?(e: FocusEvent<never>): void
  onKeyDown?(e: KeyboardEvent<never>): void
}

export interface TooltipProps {
  /** 풍선 글자. */
  content: ReactNode
  /** 단축키 표시("⌘K"). */
  shortcut?: string | undefined
  /** 감쌀 요소 하나. 초점을 받을 수 있어야 한다(단추·링크). */
  children: ReactElement<Anchor>
  /** 기본 `top`. */
  side?: PopSide
  /** 기본 `center`. */
  align?: PopAlign
  className?: string
}

export function Tooltip(props: TooltipProps) {
  const id = useId()
  const tip = useRef<HTMLSpanElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const show = () => {
    const el = tip.current
    const at = el?.previousElementSibling ?? null
    if (el === null || at === null || el.matches(':popover-open')) return
    // 다른 팝오버가 여는 도중(메뉴가 닫히며 포커스를 되돌릴 때) 불리면 브라우저가
    // InvalidStateError 를 던진다. 툴팁은 못 떠도 그만이라 삼킨다.
    try {
      el.showPopover()
    } catch {
      return
    }
    put_beside(el, at, props.side ?? 'top', props.align ?? 'center', 6)
  }
  const hide = () => {
    clearTimeout(timer.current)
    const el = tip.current
    if (el === null || !el.matches(':popover-open')) return
    el.hidePopover()
    last_hidden = performance.now()
  }
  const later = () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(show, tooltip_wait(performance.now(), last_hidden))
  }

  const enter = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') later()
  }
  const focus = (e: { target: EventTarget }) => {
    if (e.target instanceof Element && e.target.matches(':focus-visible')) show()
  }
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && tip.current?.matches(':popover-open')) {
      e.stopPropagation()
      hide()
    }
  }

  const child = props.children
  const own = child.props
  const described = [own['aria-describedby'], id].filter(Boolean).join(' ')
  return (
    <>
      {cloneElement(child, {
        'aria-describedby': described,
        title: undefined,
        onPointerEnter: (e: PointerEvent<never>) => {
          own.onPointerEnter?.(e)
          enter(e)
        },
        onPointerLeave: (e: PointerEvent<never>) => {
          own.onPointerLeave?.(e)
          hide()
        },
        onPointerDown: (e: PointerEvent<never>) => {
          own.onPointerDown?.(e)
          // 마우스는 누르면 감춘다(누른 단추의 설명이 손을 가린다). 손가락은 누르고 있는 동안이 지나가기다.
          if (e.pointerType === 'touch') later()
          else hide()
        },
        onPointerUp: (e: PointerEvent<never>) => {
          own.onPointerUp?.(e)
          if (e.pointerType === 'touch') hide()
        },
        onPointerCancel: (e: PointerEvent<never>) => {
          own.onPointerCancel?.(e)
          hide()
        },
        onFocus: (e: FocusEvent<never>) => {
          own.onFocus?.(e)
          focus(e)
        },
        onBlur: (e: FocusEvent<never>) => {
          own.onBlur?.(e)
          hide()
        },
        onKeyDown: (e: KeyboardEvent<never>) => {
          own.onKeyDown?.(e)
          keys(e)
        },
      })}
      <span
        ref={tip}
        id={id}
        popover="manual"
        role="tooltip"
        className={cx('Tooltip', props.className)}
      >
        {props.content}
        {props.shortcut && (
          <kbd className="Tooltip-shortcut" aria-hidden>
            {props.shortcut}
          </kbd>
        )}
      </span>
    </>
  )
}
